import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const IncomingDOsPage = () => {
  const [distributorDOs, setDistributorDOs] = useState([]);
  const [partnerDOs, setPartnerDOs] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dos, pdos, prods] = await Promise.all([
        api.get('/delivery-orders'),
        api.get('/partner-dos'),
        api.get('/products'),
      ]);
      setDistributorDOs(dos.data);
      setPartnerDOs(pdos.data);
      setProducts(prods.data);
    } catch (error) { console.error(error); }
  };

  const acknowledgeDO = async (doId) => {
    try {
      await api.post(`/delivery-orders/${doId}/acknowledge`);
      toast.success('DO acknowledged — stock added');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const acknowledgePartnerDO = async (doId) => {
    try {
      await api.post(`/partner-dos/${doId}/acknowledge`);
      toast.success('Partner DO verified — stock added');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const getProduct = (id) => products.find(p => p.id === id);
  const badge = (status) => {
    if (status === 'acknowledged' || status === 'verified') return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  };

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Incoming DOs</h1>

      <h2 className="font-bold mb-3" style={{ fontFamily: 'Manrope' }}>From Distributor</h2>
      <div className="space-y-3 mb-8">
        {distributorDOs.map((doItem) => (
          <Card key={doItem.id} data-testid={`incoming-do-${doItem.id}`} className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="font-mono text-xs text-slate-500">{doItem.do_number}</p>
                <p className="text-xs text-slate-500 mt-1">Issued: {new Date(doItem.issued_at).toLocaleDateString()}</p>
                <div className="mt-2 space-y-1">
                  {doItem.line_items.map((li, idx) => (
                    <div key={idx} className="text-xs text-slate-700">
                      {getProduct(li.product_id)?.name || 'Unknown'} — Qty {li.quantity}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge className={badge(doItem.status)}>{doItem.status}</Badge>
                {doItem.status !== 'acknowledged' && (
                  <Button size="sm" data-testid={`ack-do-${doItem.id}`} onClick={() => acknowledgeDO(doItem.id)} style={{ backgroundColor: '#1E3A8A' }}>
                    <CheckCircle size={14} className="mr-1" /> Acknowledge
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {distributorDOs.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No distributor DOs</p>}
      </div>

      <h2 className="font-bold mb-3" style={{ fontFamily: 'Manrope' }}>Direct from Partner</h2>
      <div className="space-y-3">
        {partnerDOs.map((doItem) => (
          <Card key={doItem.id} data-testid={`incoming-pdo-${doItem.id}`} className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="font-mono text-xs text-slate-500">{doItem.do_number}</p>
                <p className="text-xs text-slate-500 mt-1">Issued: {new Date(doItem.issued_at).toLocaleDateString()}</p>
                <div className="mt-2 space-y-1">
                  {doItem.line_items.map((li, idx) => (
                    <div key={idx} className="text-xs text-slate-700">
                      {getProduct(li.product_id)?.name || 'Unknown'} — Batch {li.batch_number} — Exp {String(li.expiry_month).padStart(2,'0')}/{li.expiry_year} — Qty {li.quantity}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge className={badge(doItem.status)}>{doItem.status}</Badge>
                {doItem.status !== 'verified' && (
                  <Button size="sm" data-testid={`ack-pdo-${doItem.id}`} onClick={() => acknowledgePartnerDO(doItem.id)} style={{ backgroundColor: '#1E3A8A' }}>
                    <CheckCircle size={14} className="mr-1" /> Confirm Receipt
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {partnerDOs.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No partner direct DOs</p>}
      </div>
    </div>
  );
};
